import {
	Component,
	Host,
	h,
	Prop,
	Watch,
	Event,
	EventEmitter,
	State,
	Listen,
	Method,
} from '@stencil/core';
import { Loader, LoaderOptions } from 'google-maps';
import { Location } from '../../interfaces/location';

declare var window;

@Component({
	tag: 'location-map',
	styleUrl: 'location-map.scss',
	shadow: true,
})
export class LocationMap {
	public icon = {
		path:
			'M16,2 C9.92792869,2.0073834 5.0073834,6.92792869 4.99998863,13 C4.99655645,15.3832648 5.77514018,17.7018374 7.2163,19.6 C7.2163,19.6 7.5163,19.9945 7.5645,20.0517 L16,30 L24.439,20.0474 C24.4834,19.9941 24.7837,19.5996 24.7837,19.5996 L24.7852,19.5972 C26.2255009,17.6997465 27.0035499,15.3821804 27.0000121,13 C26.9926166,6.92792869 22.0720713,2.0073834 16,2 Z',
		fillColor: '#0066A4',
		fillOpacity: 0.9,
		strokeWeight: 0,
		rotation: 0,
		scale: 1,
		zIndex: 1,
		labelOrigin: null, // new google.maps.Point(16, 13),
		// anchor: new google.maps.Point(15, 30),
	};

	@Prop({ mutable: true }) mapElement: HTMLElement = null;
	@Prop({ mutable: true }) map: google.maps.Map = null;
	@Prop({ mutable: false }) infoWindow: google.maps.InfoWindow = null;

	@Prop() locations: any[];
	@Prop() activeLocation: any | any[];
	@Prop() activeLocationIdentifier: string;

	@Prop() iconColor: string = '#0066A4';
	@Prop() iconActiveColor: string = '#E97400';

	@Prop() center: { latitude: number; longitude: number } = {
		latitude: 39.331844,
		longitude: -82.98177,
	};
	@Prop({ mutable: true }) zoom: number;

	@Prop() markerLabelProp: string = null;

	@State() markerIdentifierHashMap = {};
	@State() private markers: google.maps.Marker[] = [];

	@Event() loaded: EventEmitter;
	@Event() markerClicked: EventEmitter<any>;
	@Event() infoWindowClosed: EventEmitter<void>;

	componentWillLoad() {
		this.initMap();
	}

	@Method()
	async showInfoWindow(content: string, position: google.maps.LatLng) {
		if (!this.infoWindow) {
			this.infoWindow = new google.maps.InfoWindow({
				pixelOffset: new google.maps.Size(16, 16, 'px'),
			});
			this.infoWindow.addListener('closeclick', () => this.infoWindowClosed.emit());
		}
		this.infoWindow.close();

		this.infoWindow.setContent(content);
		this.infoWindow.setPosition(position);
		this.infoWindow.open(this.map);
	}

	@Listen('loaded')
	onLoaded() {
		this.updateMapLocations();
	}

	@Watch('center')
	private onCenterChange(coords: { latitude; longitude }, oldValue) {
		this.map.setCenter(new google.maps.LatLng(coords.latitude, coords.longitude));
	}

	@Watch('zoom')
	private onZoomChange(zoom: number) {
		this.map.setZoom(zoom);
	}

	@Watch('activeLocation')
	private highLightActiveLocation(newValue: any | any[], oldValue: any | any[]) {
		if (!this.activeLocationIdentifier) {
			return;
		}
		if (null !== oldValue) {
			// inactive
			if ('string' === typeof oldValue) {
				oldValue = [oldValue];
			}
			oldValue.forEach(id => {
				if (this.markerIdentifierHashMap[id]) {
					this.updateMarkerIcon(this.markerIdentifierHashMap[id], false);
				}
			});
		}
		if (null !== newValue) {
			// active
			if ('string' === typeof newValue) {
				newValue = [newValue];
			}
			newValue.forEach(id => {
				if (this.markerIdentifierHashMap[id]) {
					this.updateMarkerIcon(this.markerIdentifierHashMap[id], true);
				}
			});
		}
	}

	@Watch('locations')
	private updateMapLocations() {
		this.resetMarkers();

		this.markers = [];
		let bounds = new google.maps.LatLngBounds();
		if (!this.locations || !this.locations.length) {
			this.map.setCenter(new google.maps.LatLng(this.center.latitude, this.center.longitude));
			return;
		}
		this.locations.forEach(loc => {
			if (loc.Latitude && loc.Longitude) {
				let marker = this.setupMarker(
					new google.maps.Marker({
						position: new google.maps.LatLng(loc.Latitude, loc.Longitude),
						map: this.map,
						title: loc.Title,
						icon: { ...this.icon },
					}),
					loc,
				);
				if (this.markerLabelProp) {
					marker.setLabel({
						text: loc[this.markerLabelProp].toString(),
						color: 'transparent',
					});
				}
				bounds.extend(marker.getPosition());
			}
		});
		this.map.fitBounds(bounds);
		this.zoom = this.map.getZoom();
	}

	private updateMarkerIcon(marker: google.maps.Marker, isActive: boolean) {
		let icon: any = marker.getIcon();
		let label: any = marker.getLabel();
		if (isActive) {
			icon.fillColor = this.iconActiveColor;
			icon.fillOpacity = 1;
			if (label) {
				label.color = 'white';
			}
			marker.set('zIndex', 1000);
		} else {
			icon.fillColor = this.iconColor;
			icon.fillOpacity = 0.9;
			if (label) {
				label.color = 'transparent';
			}
			marker.set('zIndex', 1);
		}
		marker.setIcon(icon);
	}

	private setupMarker(marker: google.maps.Marker, location: Location): google.maps.Marker {
		const addListener = google.maps.event.addListener;
		addListener(marker, 'click', () => this.mapMarkerClicked(location, marker));
		addListener(marker, 'mouseover', () => this.updateMarkerIcon(marker, true));
		addListener(marker, 'mouseout', () => this.updateMarkerIcon(marker, false));
		this.markers.push(marker);
		if (this.activeLocationIdentifier) {
			this.markerIdentifierHashMap[location[this.activeLocationIdentifier]] = marker;
		}
		return marker;
	}

	private mapMarkerClicked(loc: Location, marker: google.maps.Marker) {
		this.markerClicked.emit({ marker, location: loc });
		this.updateMarkerIcon(marker, true);
		this.map.panTo(new google.maps.LatLng(loc.Latitude, loc.Longitude));
	}

	private resetMarkers() {
		if (this.markers) {
			this.markers.forEach(marker => {
				marker.setMap(null);
			});
			if (this.markerIdentifierHashMap) {
				this.markerIdentifierHashMap = {};
			}
		}
	}

	private async initMap() {
		const options: LoaderOptions = {
			/* todo */
		};
		// @todo move key to prop or window, handle existing loader/google object
		const loader = new Loader('AIzaSyBo6pmkGTbjIh7yufrTsDuYpteWfJRzYYA', options);
		const google = await loader.load();
		this.icon.labelOrigin = new google.maps.Point(16, 13);
		this.map = new google.maps.Map(this.mapElement, {
			center: { lat: this.center.latitude, lng: this.center.longitude },
			zoom: 10,
			maxZoom: 16,
		});
		this.loaded.emit();
	}

	render() {
		return (
			<Host>
				<div id="mapElement" ref={el => (this.mapElement = el as HTMLElement)}></div>
			</Host>
		);
	}
}
